<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>
        <span></span>
        <b-button
          variant="primary"
          class="btn-primary-lg"
          data-testid="report-search-button"
          @click="fetchReport"
        >
          แสดงข้อมูล
        </b-button>
      </div>

      <div class="fc-row fc-row-doctor">
        <div class="field">
          <label class="field-lbl">แพทย์</label>
          <b-input-group>
            <multiselect
              class="col p-0 multiselect-h-36px"
              v-model="selectedDoctor"
              :options="getDoctorList"
              :multiple="true"
              :limit="5"
              placeholder="พิมพ์ค้นหาแพทย์"
              track-by="nameTh"
              label="fullName"
              selectLabel=""
              selectedLabel=""
              deselectLabel="คลิกเพื่อลบออก"
            >
              <span slot="noResult">ไม่พบรายการ กรุณาเปลี่ยนคำค้นหา</span>
            </multiselect>
            <b-input-group-append>
              <b-button
                size="sm"
                class="fi-fav"
                @click="selectedDoctor = getDoctorList"
              >
                เลือกทั้งหมด
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>
        <b-dropdown
          size="sm"
          variant="outline-success"
          right
          class="align-self-end"
        >
          <template #button-content>
            <i class="fas fa-download"></i>
          </template>
          <b-dropdown-item @click="excelExport">
            <i class="fas fa-file-excel mr-2"></i>Excel
          </b-dropdown-item>
          <b-dropdown-item @click="pdfExport">
            <i class="fas fa-file-pdf mr-2"></i>PDF
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
    <div class="subtab-row">
      <div class="segmented" role="tablist" aria-label="มุมมองรายงาน">
        <button
          type="button"
          class="segmented-btn"
          :class="{ active: activeTab === 'summary' }"
          @click="activeTab = 'summary'"
        >
          📊 สรุปรวม
        </button>
        <button
          type="button"
          class="segmented-btn"
          :class="{ active: activeTab === 'daily' }"
          @click="activeTab = 'daily'"
        >
          📅 รายงานตามวัน
        </button>
      </div>
      <div v-if="dateRange[0] && dateRange[1]" class="d-flex align-items-center" style="gap: 10px;">
        <span class="range-pill">
          📆 <strong>{{ formatDate(dateRange[0]) }}</strong>
          <span style="opacity: 0.5">→</span>
          <strong>{{ formatDate(dateRange[1]) }}</strong>
        </span>
        <span style="font-size: 12px; color: #8a929c;">
          {{ filteredDoctorIncomeList.length }} แพทย์
        </span>
      </div>
    </div>

    <div v-show="activeTab === 'summary'">
      <!-- Bar chart: DF ranking -->
      <div v-if="dfRanking.length" class="chart-card">
        <div class="chart-header">
          <div>
            <div class="chart-title">อันดับ DF ของแพทย์</div>
            <div class="chart-subtitle">เรียงตามยอด DF</div>
          </div>
          <div style="display: flex; gap: 12px; font-size: 11.5px; color: #5b6470;">
            <span style="display: flex; align-items: center; gap: 5px;">
              <span style="width: 9px; height: 9px; background: #2a6df4; border-radius: 2px;"></span>
              มีรายได้
            </span>
            <span style="display: flex; align-items: center; gap: 5px;">
              <span style="width: 9px; height: 9px; background: #cdd5e2; border-radius: 2px;"></span>
              ไม่มีรายได้
            </span>
          </div>
        </div>
        <div class="chart-bars">
          <div
            v-for="(d, idx) in dfRanking"
            :key="d.uid"
            class="chart-bar-wrap"
          >
            <div class="chart-bar-track">
              <div
                class="chart-bar"
                :class="{ 'muted-bar': d.df === 0 }"
                :style="{ height: barHeight(d.df) + '%' }"
              ></div>
            </div>
            <div class="chart-bar-label">
              {{ shortName(d.name) }}
            </div>
            <div class="chart-tooltip">
              {{ d.name }} — <strong>{{ formatNumber(d.df) }}</strong>
            </div>
            <span v-if="idx < 0"></span><!-- vue-template-compiler unused-var workaround if needed -->
          </div>
        </div>
      </div>

      <h6 class="mt-2">
        รายงานของวันที่
        {{ formatDate(dateRange[0]) + " - " + formatDate(dateRange[1]) }}
      </h6>
      <div data-testid="report-table">
        <b-table
          id="doctors-summary-table"
          class="mt-2 report-table"
          small
          sort-icon-left
          head-variant="light"
          :busy="tableBusy"
          :items="filteredDoctorIncomeList"
          :fields="visibleSumFields"
          :responsive="true"
          show-empty
          sticky-header="calc(100vh - 210px)"
        >
          <template #cell(index)="row">{{ row.index + 1 }}.</template>
          <template #cell(user.fullName)="row">
            <span class="doctor-cell">
              <span
                class="doctor-avatar report-export-skip"
                :style="{ background: doctorColor(row.item.uid) }"
              >{{ doctorInitials(row.item.user.fullName) }}</span>
              <span>{{ row.item.user.fullName }}</span>
            </span>
          </template>
          <template #cell(calculatedFee)="row">
            <span data-testid="report-fee">({{ formatNumber(getTotalFeeForDoctor(row.item.uid)) }})</span>
          </template>
          <template #cell(calculatedNetDf)="row">
            <span data-testid="report-total">{{ formatNumber(row.item.sumReport.paidDf - getTotalFeeForDoctor(row.item.uid) - row.item.sumReport.labDfAmount) }}</span>
          </template>
          <template #cell(sumReport.paidDf)="row">
            <span class="bar-cell">
              <span class="bar-track">
                <span
                  class="bar-fill"
                  :style="{ width: barWidth(row.item.sumReport.paidDf) + '%' }"
                ></span>
              </span>
              <span class="bar-value" data-testid="report-paid-df">
                {{ formatNumber(row.item.sumReport.paidDf) }}
              </span>
            </span>
          </template>
        </b-table>
      </div>
    </div>

    <div v-show="activeTab === 'daily'">
      <div id="doctorsIncomeTable" ref="doctorsIncomeTable" class="mt-2">
          <div v-for="doctor in filteredDoctorIncomeList" :key="doctor.uid">
            <h6>แพทย์: {{ doctor.user.fullName }}</h6>

            <!-- Income Table -->
            <b-table
              :id="'doctors-report-' + doctor.uid"
              class="report-table"
              small
              sort-icon-left
              head-variant="light"
              :busy="tableBusy"
              :items="doctor.reportDoctorIncomeList"
              :fields="visibleDailyFields"
              sort-by="creationDt"
              v-model="visibleRows"
              :sort-desc="true"
              :responsive="true"
              show-empty
              sticky-header="calc(100vh - 210px)"
            >
              <template #cell(index)="row">{{ row.index + 1 }}.</template>
              <template #cell(creationDt)="row">{{
                formatDate(row.value)
              }}</template>
              <template #cell(dfNet)="row">{{
                formatNumber(row.item.paidDf - row.item.labDfAmount)
              }}</template>
              <template #custom-foot>
                <!-- Old layout (before cutoff) -->
                <b-tr v-if="!isAfterCutoff" variant="secondary" class="text-bold text-right">
                  <td class="text-left">รวม</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.paid) }}</td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.processingFeeBaht) }}</td>
                  <td>{{ formatNumber(doctor.sumReport.paidDf) }}</td>
                  <td>{{ formatNumber(doctor.sumReport.paidCf) }}</td>
                  <td>{{ formatNumber(doctor.sumReport.labDfAmount) }}</td>
                  <td>{{ formatNumber(doctor.sumReport.labCfAmount) }}</td>
                  <td>{{ formatNumber(doctor.sumReport.totalReceived) }}</td>
                </b-tr>
                <!-- New layout (after cutoff): 9 visible cols
                     index, creationDt, price, finalDiscount, amount, paid,
                     overdue, paidDf, labDfAmount -->
                <b-tr v-else variant="secondary" class="text-bold text-right">
                  <td class="text-left">รวม DF</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.paid) }}</td>
                  <td></td>
                  <td>{{ formatNumber(doctor.sumReport.paidDf) }}</td>
                  <td class="text-danger">
                    ({{ formatNumber(doctor.sumReport.labDfAmount) }})
                  </td>
                </b-tr>
              </template>
              <template #empty="">
                <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
              </template>
            </b-table>

            <!-- Fee Summary Section (after cutoff only) -->
            <b-card v-if="isAfterCutoff && getDfByPaymentForDoctor(doctor.uid).length > 0" class="mt-3" no-body>
              <b-card-header class="py-2">
                <strong>สรุปค่าธรรมเนียมตามวิธีชำระ</strong>
              </b-card-header>
              <b-card-body class="p-0">
                <b-table-simple small responsive hover>
                  <b-thead head-variant="light">
                    <b-tr class="text-right">
                      <b-th class="text-left">วิธีชำระ</b-th>
                      <b-th>DF</b-th>
                      <b-th>ค่าธรรมเนียม %</b-th>
                      <b-th>ค่าธรรมเนียม</b-th>
                    </b-tr>
                  </b-thead>
                  <b-tbody>
                    <b-tr v-for="(pm, idx) in getDfByPaymentForDoctor(doctor.uid)" :key="idx" class="text-right">
                      <b-td class="text-left">{{ pm.name }}</b-td>
                      <b-td>{{ formatNumber(pm.df) }}</b-td>
                      <b-td>{{ pm.feePercent }}%</b-td>
                      <b-td class="text-danger">({{ formatNumber(pm.feeAmount) }})</b-td>
                    </b-tr>
                  </b-tbody>
                  <b-tfoot>
                    <b-tr variant="secondary" class="text-bold text-right">
                      <b-td class="text-left">รวมค่าธรรมเนียม</b-td>
                      <b-td></b-td>
                      <b-td></b-td>
                      <b-td class="text-danger">({{ formatNumber(getTotalFeeForDoctor(doctor.uid)) }})</b-td>
                    </b-tr>
                  </b-tfoot>
                </b-table-simple>
              </b-card-body>
            </b-card>

            <!-- Lab Summary Section (after cutoff only) -->
            <b-card v-if="isAfterCutoff" class="mt-3" no-body>
              <b-card-header class="py-2">
                <strong>สรุปค่าแลป</strong>
              </b-card-header>
              <b-card-body class="py-2">
                <b-row>
                  <b-col class="text-right">
                    <span>รวมค่าแลป DF: </span>
                    <strong class="text-danger">({{ formatNumber(doctor.sumReport.labDfAmount) }})</strong>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>

            <!-- Total Summary Section (after cutoff only) -->
            <div v-if="isAfterCutoff" class="summary-strip mt-3">
              <span class="label-lead">📈 สรุป DF สุทธิของแพทย์</span>
              <div class="formula">
                <span class="item">
                  <span class="label">DF:</span>
                  <span class="value">{{ formatNumber(doctor.sumReport.paidDf) }}</span>
                </span>
                <span class="op">−</span>
                <span class="item">
                  <span class="label">ค่าธรรมเนียม:</span>
                  <span class="value negative">{{ formatNumber(getTotalFeeForDoctor(doctor.uid)) }}</span>
                </span>
                <span class="op">−</span>
                <span class="item">
                  <span class="label">ค่าแลป:</span>
                  <span class="value negative">{{ formatNumber(doctor.sumReport.labDfAmount) }}</span>
                </span>
                <span class="op">=</span>
                <span class="item grand">
                  <span class="label">DF สุทธิ:</span>
                  <span class="value">{{ formatNumber(doctor.sumReport.paidDf - getTotalFeeForDoctor(doctor.uid) - doctor.sumReport.labDfAmount) }}</span>
                </span>
              </div>
            </div>

            <hr class="my-4" />
          </div>
        </div>
      </div>

    <!-- <div class="d-none">
      <div ref="header" id="printHeader">
        <h4>รายงานรายได้แพทย์ทุกท่าน</h4>
        <h6>
          วันที่
          {{
            formatDate(dateRange.startDate) +
            "-" +
            formatDate(dateRange.endDate)
          }}
        </h6>
        <span class="text-right">
          พิมพ์วันที่ {{ formatDate(today) }} จัดทำโดย
          {{ userInfo.firstNameTh + " " + userInfo.lastNameTh }}</span
        >
      </div>
      <div ref="footer" id="printFooter">
        <h3>Footer</h3>
      </div>
    </div> -->
    <!-- <PdfExport ref="pdfExport" /> -->
    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";

// import PdfExport from "@/components/PdfExport";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import moment from "moment";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

export default {
  name: "BranchItemsIncomeReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,

      dateRange: [],
      selectedDoctor: [],
      activeTab: "summary", // "summary" | "daily"

      //table
      tableBusy: false,
      doctorsIncomeList: [],
      fields: [
        {
          key: "index",
          label: "",
          class: "narrow-spacing",
        },
        {
          key: "creationDt",
          label: "วันที่",
          sortable: true,
          class: "narrow-spacing",
        },
        {
          key: "price",
          label: "ค่ารักษา",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "finalDiscount",
          label: "ส่วนลด",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "amount",
          label: "ค่ารักษาสุทธิ",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "paid",
          label: "ชำระ",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "overdue",
          label: "ค้างชำระ",
          class: "text-right text-danger narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "paidDf",
          label: "DF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "paidCf",
          label: "CF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "labDfAmount",
          label: "Lab DF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "labCfAmount",
          label: "Lab CF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "dfNet",
          label: "DF สุทธิ",
          class: "text-right narrow-spacing font-weight-bold",
        },
      ],
      visibleRows: [],
      sumFields: [
        {
          key: "index",
          label: "",
          class: "narrow-spacing",
        },
        {
          key: "user.fullName",
          label: "แพทย์",
          sortable: true,
          class: "narrow-spacing",
        },

        {
          key: "sumReport.paid",
          label: "ชำระ",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },

        {
          key: "sumReport.processingFeeBaht",
          label: "ค่าธรรมเนียม",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "sumReport.paidDf",
          label: "DF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "sumReport.paidCf",
          label: "CF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "sumReport.labDfAmount",
          label: "Lab DF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "sumReport.labCfAmount",
          label: "Lab CF",
          class: "text-right narrow-spacing",
          formatter: "formatNumber",
        },
        {
          key: "sumReport.totalReceived",
          label: "DF สุทธิ",
          class: "text-right narrow-spacing font-weight-bold",
          formatter: "formatNumber",
        },
      ],
      sumKeys: ["paid", "processingFeeBaht", "paidDf", "paidCf", "labDfAmount", "labCfAmount", "labTotalExpense"],

      loadingTime: 0,

      // DF by payment type data (for after cutoff)
      dfByPaymentData: [],
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      userInfo: "moduleUser/getUserInfo",
      getDoctorList: "moduleUser/getDoctorList",
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      paymentMethodList: "moduleUser/getPaymentMethodList",
      branchInfo: "moduleBranch/getBranchInfo",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    dateFrom() {
      return this.dateRange[0]
        ? moment(this.dateRange[0]).startOf("day").format()
        : "";
    },
    dateTo() {
      return this.dateRange[1]
        ? moment(this.dateRange[1]).endOf("day").format()
        : "";
    },
    today() {
      let today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
    filteredDoctorIncomeList() {
      let listAllDoctors = this.doctorsIncomeList;
      listAllDoctors.forEach(doctor => {
        doctor.sumReport = this.sumKeys.reduce((accumulator, value) => {
          return { ...accumulator, [value]: 0 };
        }, {});

        this.sumKeys.forEach(field => {
          doctor.sumReport[field] = this.calculateSum(
            doctor.reportDoctorIncomeList,
            field
          );
        });

        // Calculate total amount doctor receives (paidDf - labDfAmount)
        doctor.sumReport.totalReceived = 
          doctor.sumReport.paidDf - 
          doctor.sumReport.labDfAmount;
      });
      let selectedUid = this.selectedDoctor.map(i => i.uid);
      return listAllDoctors.filter(doctor => selectedUid.includes(doctor.uid));
    },

    // Check if date range is after cutoff (1 Feb 2026)
    isAfterCutoff() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-02-01").startOf("day");
      return moment(this.dateRange[0]).startOf("day").isSameOrAfter(cutoff);
    },

    // Check if date range crosses cutoff
    isCrossDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) return false;
      const cutoff = moment("2026-02-01").startOf("day");
      const start = moment(this.dateRange[0]).startOf("day");
      const end = moment(this.dateRange[1]).startOf("day");
      return start.isBefore(cutoff) && end.isSameOrAfter(cutoff);
    },

    // Visible fields for summary table based on date
    visibleSumFields() {
      if (this.isAfterCutoff) {
        return [
          { key: "index", label: "", class: "narrow-spacing" },
          { key: "user.fullName", label: "แพทย์", sortable: true, class: "narrow-spacing" },
          { key: "sumReport.paid", label: "ชำระ", class: "text-right narrow-spacing", formatter: "formatNumber" },
          { key: "sumReport.paidDf", label: "DF", class: "text-right narrow-spacing", formatter: "formatNumber" },
          { key: "calculatedFee", label: "ค่าธรรมเนียม", class: "text-right narrow-spacing text-danger", formatter: "formatNumber" },
          { key: "sumReport.labDfAmount", label: "ค่าแลป", class: "text-right narrow-spacing text-danger", formatter: "formatNumber" },
          { key: "calculatedNetDf", label: "DF สุทธิ", class: "text-right narrow-spacing font-weight-bold", formatter: "formatNumber" },
        ];
      }
      return this.sumFields;
    },

    // Visible fields for daily table based on date
    visibleDailyFields() {
      if (this.isAfterCutoff) {
        return this.fields.filter(f =>
          !["processingFeeBaht", "paidCf", "labCfAmount", "dfNet"].includes(f.key)
        );
      }
      return this.fields;
    },

    // Get DF by payment data for a specific doctor
    getDfByPaymentForDoctor() {
      return (doctorUid) => {
        const doctorData = this.dfByPaymentData.find(d => d.doctorUid === doctorUid);
        if (!doctorData) return [];

        const paymentTotals = {};
        doctorData.dates?.forEach(date => {
          date.dfByPaymentType?.forEach(pm => {
            if (pm.value > 0) {
              if (!paymentTotals[pm.keyName]) {
                paymentTotals[pm.keyName] = {
                  name: pm.name,
                  keyName: pm.keyName,
                  df: 0,
                  feePercent: this.getProcessingFee(pm.keyName),
                };
              }
              paymentTotals[pm.keyName].df += pm.value;
            }
          });
        });

        return Object.values(paymentTotals).map(pm => ({
          ...pm,
          feeAmount: (pm.df * pm.feePercent) / 100,
          netDf: pm.df - (pm.df * pm.feePercent) / 100,
        }));
      };
    },

    // Calculate total fee for a doctor from DfByPaymentType data
    getTotalFeeForDoctor() {
      return (doctorUid) => {
        const paymentData = this.getDfByPaymentForDoctor(doctorUid);
        return paymentData.reduce((sum, pm) => sum + pm.feeAmount, 0);
      };
    },

    // DF ranking for the chart at top of summary view
    dfRanking() {
      return [...this.filteredDoctorIncomeList]
        .map(d => ({
          uid: d.uid,
          name: d.user.fullName,
          df: d.sumReport.paidDf || 0,
        }))
        .sort((a, b) => b.df - a.df);
    },
    maxDf() {
      return Math.max(1, ...this.dfRanking.map(d => d.df));
    },
  },
  watch: {},
  methods: {
    ...mapActions({
      fetchReportBranchDoctorIncome:
        "moduleReport/fetchReportBranchDoctorIncome",
      fetchDfByPaymentType: "moduleReport/fetchDfByPaymentType",
      fetchPaymentMethodList: "moduleUser/fetchPaymentMethodList",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    init() {
      this.fetchPaymentMethodList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    moment() {
      return moment();
    },
    getProcessingFee(keyName) {
      const method = this.paymentMethodList.find(m => m.id === keyName);
      return method?.processingFee || 0;
    },
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num)) return "-";
      return Number(num).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    doctorInitials(fullName) {
      if (!fullName) return "??";
      const parts = String(fullName).trim().split(/\s+/).filter(Boolean);
      if (parts.length === 0) return "??";
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    doctorColor(uid) {
      // Stable hue derived from uid → fixed-saturation oklch palette.
      const palette = [
        "#2a6df4", "#1d8e5b", "#a05a17", "#5b21b6",
        "#a01a3c", "#0f6e8a", "#c2521e", "#3a7e2c",
      ];
      const key = String(uid || "");
      let hash = 0;
      for (let i = 0; i < key.length; i++) {
        hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
      }
      return palette[hash % palette.length];
    },
    barWidth(value) {
      const v = Number(value) || 0;
      return Math.max(0, Math.min(100, (v / this.maxDf) * 100));
    },
    barHeight(value) {
      const v = Number(value) || 0;
      return v === 0 ? 2 : Math.max(2, (v / this.maxDf) * 100);
    },
    shortName(name) {
      if (!name) return "";
      return String(name).split(/\s+/)[0].slice(0, 8);
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("D MMM YY");
      } else {
        return "";
      }
    },
    resetDateRange() {
      this.dateRange = {
        startDate: null,
        endDate: null,
      };
    },
    showDialogToast(variant, textDetail) {
      this.$refs.Dialog.showToast(variant, textDetail);
    },
    sum(arg, row) {
      let num = row.reduce((accum, item) => {
        return accum + parseFloat(item[arg]);
      }, 0.0);
      return Math.round(num * 100) / 100;
    },
    calculateSum(array, property) {
      const total = array.reduce((accumulator, object) => {
        return accumulator + object[property];
      }, 0);

      return total;
    },
    sendEmail() {
      //รอ api
    },
    async fetchReport() {
      if (this.dateFrom === "" || this.dateTo === "") return;

      // Block cross-cutoff date range
      if (this.isCrossDateRange) {
        this.showDialogToast("warning", "ไม่สามารถ query ข้ามช่วง 1 ก.พ. 69 ได้ กรุณาเลือกช่วงวันที่ใหม่");
        return;
      }

      this.isLoading = true;
      this.setStateReportDateRange(this.dateRange);

      try {
        // Fetch main income report
        const res = await this.fetchReportBranchDoctorIncome({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          dateFrom: this.dateFrom,
          dateTo: this.dateTo,
        });
        this.doctorsIncomeList = res.data.report;

        // If after cutoff, also fetch DF by payment type
        if (this.isAfterCutoff) {
          const dfRes = await this.fetchDfByPaymentType({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            groupBy: "day",
          });
          this.dfByPaymentData = dfRes.data.item || [];
        } else {
          this.dfByPaymentData = [];
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },
    buildExportSheets() {
      const sheets = [{ tableId: "doctors-summary-table", name: "สรุปรวม" }];
      this.filteredDoctorIncomeList.forEach(i => {
        sheets.push({
          tableId: "doctors-report-" + i.uid,
          name: i.user.fullName,
          doctorName: i.user.fullName,
        });
      });
      return sheets;
    },
    excelExport() {
      exportTableToExcel({
        reportTitle: "รายงานรายได้แพทย์ (รวมทุกคน)",
        reportNameEn: "DoctorIncome",
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets: this.buildExportSheets(),
      });
    },
    pdfExport() {
      exportReportToPdf({
        reportTitle: "รายงานรายได้แพทย์ (รวมทุกคน)",
        reportNameEn: "DoctorIncome",
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        branchName: this.branchInfo && this.branchInfo.name,
        clinicName: this.clinicInfo && this.clinicInfo.name,
        sheets: this.buildExportSheets(),
      });
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
  },
  mounted() {
    this.selectedDoctor = this.getDoctorList;

    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
  updated() {},
};
</script>

<style>
</style>
